import { useEffect, useRef, DependencyList } from 'react';
import { AppSettings } from '@/Infrastructure/Configuration/types';
import { useAppSelector } from '@reduxStore';

type DebouncedFunc = (...args: unknown[]) => void;
type UseDebouncerEffect = () => void;

const useDebounce = (func: DebouncedFunc, delayMilliseconds?: number) => {
  const { defaultDebounceMs } = useAppSelector((state) => state.appSettings as AppSettings);

  const delay = delayMilliseconds ?? defaultDebounceMs;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedFuncRef = useRef<DebouncedFunc>(func);

  useEffect(() => {
    debouncedFuncRef.current = func;
  }, [func]);

  const debounced = (...args: unknown[]) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      debouncedFuncRef.current(...args);
    }, delay);
  };

  /** Cancel the pending timer and immediately invoke the debounced function */
  const flush = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      debouncedFuncRef.current();
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { debounced, flush };
};

/** Debouncer used to delay an action such as input text onchange firing too frequently. If no delay argument is passed in, the default value is used.
	Example Use:
	useDebouncer(() => {
		//Do Effect
	}, [<dependencies>], <waitTimeInMilliseconds>);
 */
const useDebouncer = (effect: UseDebouncerEffect, deps: DependencyList, delay?: number) => {
  const { debounced: debouncedEffect, flush } = useDebounce(effect, delay);

  useEffect(() => {
    debouncedEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return flush;
};

export default useDebouncer;
